import {Component, forwardRef, OnInit, Input, OnChanges, SimpleChanges, ElementRef, ViewChild} from '@angular/core';
import { NG_VALUE_ACCESSOR, Validators, FormGroup, FormControl, FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {AbstractControlValueAccessor} from '../abstract-control-value-accessor';
import {UntilDestroy} from '@ngneat/until-destroy';
import {setupUntilDestroy} from '@portal-workspace/grow-ui-library';
import {Subscription} from 'rxjs';
import {distinctUntilChanged, tap} from 'rxjs/operators';
import {MARK, Mark} from '@portal-workspace/grow-ui-library/mark';

import {AssetConditionValue, AssetConditionValueOptions, getAssetConditionOptions, compareMatch, isTier1Asset, isTier2Asset, isTier3Asset, isTier4Asset} from '@portal-workspace/grow-shared-library';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';

@UntilDestroy()
@Component({
    selector: 'asset-condition',
    templateUrl: './asset-condition.component.html',
    styleUrls: ['./asset-condition.component.scss'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AssetConditionComponent), multi: true },
        { provide: MARK, useExisting: forwardRef(() => AssetConditionComponent) },
    ],
    standalone: true,
    imports: [FormsModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, MatOptionModule]
})
export class AssetConditionComponent extends AbstractControlValueAccessor<AssetConditionValue> implements  OnInit, OnChanges, Mark {

  @Input() assetCategory: string | null = null;
  @Input() assetYear: number | null = null;
  // When true (e.g. the pricing tab), the tier 1/2 "New" year restriction is bypassed so the
  // condition can be overridden regardless of asset age.
  @Input() allowOverride = false;
  @Input() override disabled = false;

  @ViewChild('asset_condition_hint') hintElementRef!: ElementRef;

  compareWithFn: (option: AssetConditionValue, selected: AssetConditionValue) => boolean = (option, selected) => {
    return (!!option && !!selected && option.type === selected.type);
  };

  options = AssetConditionValueOptions;
  assetTier: number | null = null;
  warningHint: string | null = null;
  // True when "Used" is the only valid option (tier 1/2 older asset, no override) — field is disabled.
  usedOnly = false;

  formGroup: FormGroup<{
    control: FormControl<AssetConditionValue>
  }>
  formControl: FormControl<AssetConditionValue>;
  subscription: Subscription;

  constructor(private formBuilder: FormBuilder) {
    super();
    this.formControl = formBuilder.control(null, [Validators.required]);
    this.formGroup = formBuilder.group({
      control: this.formControl,
    });
    this.subscription = this.formControl.valueChanges.pipe(
      distinctUntilChanged(compareMatch),
      tap( (r: AssetConditionValue)  => {
        this.propagateChange(r);
        this.updateWarningHint();
      })
    ).subscribe();
  }

  private updateWarningHint(): void {
    if (!this.formControl.value || !this.assetCategory) {
      this.warningHint = null;
      return;
    }

    // For tier 3 and tier 4, show warning if marked as "New" and year is in the past
    if ((this.assetTier === 3 || this.assetTier === 4) && this.formControl.value.type === 'New') {
      const currentYear = new Date().getFullYear();
      if (this.assetYear && this.assetYear < currentYear) {
        this.warningHint = 'Note: Asset year is in the past - consider selecting "Used" instead';
        this.animateHint();
      } else {
        this.warningHint = null;
      }
    } else {
      this.warningHint = null;
    }
  }

  ngOnInit(): void {
    setupUntilDestroy(this);
    this.updateAssetTier();
    this.updateOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['assetCategory'] || changes['assetYear'] || changes['allowOverride']) {
      this.updateAssetTier();
      this.updateOptions();
      this.updateWarningHint();
    }
  }

  private updateAssetTier(): void {
    if (!this.assetCategory) {
      this.assetTier = null;
      return;
    }

    if (isTier1Asset(this.assetCategory)) {
      this.assetTier = 1;
    } else if (isTier2Asset(this.assetCategory)) {
      this.assetTier = 2;
    } else if (isTier3Asset(this.assetCategory)) {
      this.assetTier = 3;
    } else if (isTier4Asset(this.assetCategory)) {
      this.assetTier = 4;
    } else {
      this.assetTier = null;
    }
  }

  private updateOptions(): void {
    this.options = getAssetConditionOptions(this.assetTier, this.assetYear, this.allowOverride);
    this.usedOnly = this.options.length === 1 && this.options[0]?.type === 'Used';
    this.reconcileSelectedOption();
  }

  // Keep the selection consistent with the currently available options. When only one option is
  // valid (e.g. tier 1/2 older asset => "Used" only) it is auto-selected; an otherwise invalid
  // selection (e.g. "New" chosen then the year changed) is cleared so the user must re-choose.
  private reconcileSelectedOption(): void {
    const current = this.formControl.value;
    const currentIsValid = !!current && this.options.some(o => o.type === current.type);
    if (currentIsValid) {
      return;
    }
    this.formControl.setValue(this.options.length === 1 ? this.options[0] : null);
  }

  doWriteValue(v: AssetConditionValue | null | undefined): void | AssetConditionValue {
    if (v) {
      const op = this.options.find(o => o.type === v.type);
      if (op) {
        this.formControl.setValue(op);
        return op;
      }
    }
    return undefined;
  }
  mark() {
    this.formGroup.markAllAsTouched();
  }



  async animateHint() {

    if (this.formControl.errors) { // removes error component so hints can be displayed
      this.formControl.setErrors(null);
    }

    const growShrinkKeyframes = [ // Animation
      { transform: 'scale(1)', color: 'inherit' },
      { offset: 0.05, color: 'whitesmoke', backgroundColor: 'var(--grow-primary-palette-500)'},
      { offset: 0.95, color: 'whitesmoke', backgroundColor: 'var(--grow-primary-palette-500)'},
      { transform: 'scale(1)', color: 'inherit' }
    ];

    const growShrinkOptions: KeyframeAnimationOptions = {
      duration: 5000, // milliseconds
      iterations: 1
    };

    await new Promise(resolve => setTimeout(resolve, 10));

    console.log('***** animateHint');
    const element = this.hintElementRef.nativeElement;
    element?.parentElement?.parentElement?.animate(growShrinkKeyframes, growShrinkOptions);
  }
}
